'use client';

/**
 * The kids module's own navigation.
 *
 * Two shapes, one component:
 *   • desktop  — a sticky top bar with the reward counters on the right
 *   • mobile   — a bottom tab bar, because a child on a tablet holds the device
 *                at the bottom and a top nav is a stretch away
 *
 * Every target is at least 48×48 CSS pixels, which is the accessibility floor
 * for touch and the practical floor for a six-year-old's finger.
 */
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Home,
  Map,
  BarChart3,
  Gift,
  Users,
  Settings,
  ChevronDown,
  Check,
  ArrowLeft,
} from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import Avatar from './Avatar';
import { rankFor } from '../lib/metrics';
import { CoinBadge } from './ui';

const TABS = [
  { href: '/kids', label: 'Home', icon: Home, exact: true },
  { href: '/kids/journey', label: 'Journey', icon: Map },
  { href: '/kids/progress', label: 'Progress', icon: BarChart3 },
  { href: '/kids/rewards', label: 'Rewards', icon: Gift },
  { href: '/kids/parents', label: 'Grown-ups', icon: Users },
];

function isActive(pathname: string | null, href: string, exact?: boolean) {
  if (!pathname) return false;
  return exact ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);
}

export default function KidsNav() {
  const pathname = usePathname();
  const { profile, hydrated, store, switchProfile } = useKids();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const rank = rankFor(profile?.xp ?? 0);

  return (
    <>
      {/* ── top bar ─────────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-40 k-no-print"
        style={{ background: 'var(--background)', borderBottom: '2px solid var(--border)' }}
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
          {/* The way out.
              The kids module looks nothing like the rest of TypeSwift — that is
              deliberate — but arriving somewhere that different with no visible
              exit is disorienting, and the only route home used to be buried in
              the avatar dropdown. This is a plain, permanent escape hatch, kept
              visually quiet so it never competes with the child's own nav. */}
          <Link
            href="/"
            className="k-btn k-btn-ghost k-btn-sm shrink-0 !px-2 sm:!px-3"
            aria-label="Leave the kids section and go back to TypeSwift"
            title="Back to TypeSwift"
          >
            <ArrowLeft size={17} aria-hidden="true" />
            <span className="hidden lg:inline text-[0.8rem]">TypeSwift</span>
          </Link>

          <span
            className="hidden sm:block self-stretch my-3 w-px shrink-0"
            style={{ background: 'var(--border)' }}
            aria-hidden="true"
          />

          <Link
            href="/kids"
            className="flex items-center gap-2 shrink-0"
            aria-label="Kids Typing Journey home"
          >
            <span
              className="grid place-items-center rounded-2xl text-xl"
              style={{ width: 38, height: 38, background: 'var(--k-grape)', color: '#fff' }}
              aria-hidden="true"
            >
              ⌨️
            </span>
            <span className="hidden sm:block leading-tight">
              <span className="block font-extrabold text-[0.95rem]">Typing Journey</span>
              <span className="block text-[0.68rem] font-bold text-faint uppercase tracking-wider">
                for kids
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4" aria-label="Kids sections">
            {TABS.map((tab) => {
              const active = isActive(pathname, tab.href, tab.exact);
              return (
                <Link
                  key={tab.href}
                  href={tab.href}
                  aria-current={active ? 'page' : undefined}
                  className="k-btn k-btn-ghost k-btn-sm"
                  style={
                    active
                      ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
                      : undefined
                  }
                >
                  <tab.icon size={16} aria-hidden="true" />
                  {tab.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex-1" />

          {hydrated && profile && (
            <div className="flex items-center gap-2">
              <span
                className="k-pill hidden sm:inline-flex"
                style={{ background: 'var(--k-tangerine-soft)', color: 'var(--k-tangerine)' }}
                aria-label={`${profile.streak.current} day streak`}
              >
                <span aria-hidden="true">🔥</span>
                <span className="tabular-nums">{profile.streak.current}</span>
              </span>
              <CoinBadge coins={profile.coins} />

              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 rounded-full p-1 pr-2 transition-colors hover:bg-muted"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label={`Profile menu for ${profile.name}`}
                >
                  <Avatar config={profile.avatar} size={34} />
                  <ChevronDown size={15} aria-hidden="true" />
                </button>

                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-64 k-card-lg p-2 z-50"
                    style={{ boxShadow: 'var(--k-shadow-lg)' }}
                  >
                    <div className="px-3 py-2.5 flex items-center gap-3">
                      <Avatar config={profile.avatar} size={40} />
                      <div className="min-w-0">
                        <div className="font-extrabold truncate">
                          {profile.name} {profile.equipped.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {rank.rank.emoji} {rank.rank.name} · {profile.xp.toLocaleString()} XP
                        </div>
                      </div>
                    </div>

                    {store.profiles.length > 1 && (
                      <div className="mt-1 pt-1" style={{ borderTop: '2px solid var(--border)' }}>
                        <p className="k-eyebrow px-3 py-1.5">Switch typist</p>
                        {store.profiles.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              switchProfile(p.id);
                              setMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm font-bold hover:bg-muted"
                          >
                            <Avatar config={p.avatar} size={24} />
                            <span className="flex-1 truncate">{p.name}</span>
                            {p.id === profile.id && <Check size={15} aria-hidden="true" />}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-1 pt-1" style={{ borderTop: '2px solid var(--border)' }}>
                      <Link
                        href="/kids/settings"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold hover:bg-muted"
                      >
                        <Settings size={15} aria-hidden="true" /> Settings &amp; access
                      </Link>
                      <Link
                        href="/"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold hover:bg-muted"
                      >
                        ↩︎ Back to TypeSwift
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ── bottom tabs (mobile) ────────────────────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 k-no-print"
        aria-label="Kids sections"
        style={{
          background: 'var(--card)',
          borderTop: '2px solid var(--border)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <ul className="grid grid-cols-5">
          {TABS.map((tab) => {
            const active = isActive(pathname, tab.href, tab.exact);
            return (
              <li key={tab.href}>
                <Link
                  href={tab.href}
                  aria-current={active ? 'page' : undefined}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 text-[0.65rem] font-extrabold"
                  style={{
                    minHeight: 58,
                    color: active ? 'var(--accent)' : 'var(--muted-foreground)',
                  }}
                >
                  <tab.icon size={20} aria-hidden="true" />
                  {tab.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
